import { AssessmentClient } from '../lib/assessment-client';
import * as Api from '../lib/typescript-axios';
import { AssessmentEngine } from './assessment-engine';
import { AssessmentTypeIri } from './assessment-type-iri';
import { ChildAssessmentEngine } from './child-assessment-engine';
import { PersonalityAssessmentEngine } from './personality-assessment-engine';
import { ReportEngine, isReportStatus } from './report-engine';
import { getIdFromIri } from './utils';

export type EngineType = ReportEngine | AssessmentEngine | PersonalityAssessmentEngine | ChildAssessmentEngine;

export class AssessmentEngineFactory {
  private static async getAssessment(
    basePath: string,
    userId: string,
    token: string,
    productConfigurationId: string,
    assessmentId?: string,
    locale?: string,
    parentAssessmentId?: string,
  ): Promise<Api.AssessmentFullRead> {
    const client = new AssessmentClient(basePath, token, locale);

    if (assessmentId) {
      return client.getAssessment(assessmentId);
    }

    return client.startAssessment(userId, productConfigurationId, parentAssessmentId);
  }

  private static async getMinimumAsessment(
    basePath: string,
    token: string,
    assessmentId: string,
    locale?: string,
  ): Promise<Api.AssessmentMinimumRead> {
    const client = new AssessmentClient(basePath, token, locale);

    return client.getMinimumAssessment(assessmentId);
  }

  public static async getPersonalityAssessmentEngine(
    client: AssessmentClient,
    assessment: Api.AssessmentFullRead,
    userId: string,
    token: string,
    hasCareers: boolean,
    hasBadges: boolean,
  ): Promise<PersonalityAssessmentEngine> {
    if (assessment.productConfiguration.assessmentType !== AssessmentTypeIri.PERSONALITY_ASSESSMENT_TYPE_IRI) {
      throw new Error('invalid assessment type');
    }

    const [careerClusters, careerClusterRatings] = await Promise.all([client.getCareerClusters(), client.getCareerClusterRatings()]);

    return new PersonalityAssessmentEngine(userId, client, assessment, careerClusters, careerClusterRatings, hasCareers, hasBadges);
  }

  public static async getAssessmentEngine(
    basePath: string,
    userId: string,
    token: string,
    productConfigurationId: string,
    hasCareers: boolean,
    hasBadges: boolean,
    enableReportBlocking: boolean,
    assessmentId?: string,
    locale?: string,
    parentAssessmentId?: string,
  ): Promise<EngineType> {
    const assessmentClient: AssessmentClient = new AssessmentClient(basePath, token, locale);
    let assessmentMinimumRead: Api.AssessmentMinimumRead | null = null;
    let assessmentFullRead: Api.AssessmentFullRead | null = null;

    if (assessmentId) {
      assessmentMinimumRead = await AssessmentEngineFactory.getMinimumAsessment(basePath, token, assessmentId, locale);

      if (!assessmentMinimumRead) {
        throw new Error(`Assessment not found or not accessible (assessmentId: ${assessmentId})`);
      }

      if (
        assessmentMinimumRead.status === ChildAssessmentEngine.STATE_RETURN_TO_PARENT_ASSESSMENT &&
        assessmentMinimumRead.parentAssessment != null
      ) {
        const parentId = assessmentMinimumRead.parentAssessment;

        assessmentMinimumRead = await this.getMinimumAsessment(basePath, token, parentId, locale);

        if (!assessmentMinimumRead) {
          throw new Error(`Parent assessment not found or not accessible (parentAssessmentId: ${parentId})`);
        }
      }

      if (isReportStatus(assessmentMinimumRead.status) && assessmentMinimumRead.achieveWorksReport) {
        const achieveWorksReportId = getIdFromIri(assessmentMinimumRead.achieveWorksReport);

        return ReportEngine.getReportEngine(
          basePath,
          userId,
          assessmentMinimumRead.status,
          token,
          achieveWorksReportId,
          hasCareers,
          hasBadges,
          enableReportBlocking,
          assessmentMinimumRead.isResultReviewed,
          locale,
        );
      }
    }

    // create new assessment
    assessmentFullRead = await AssessmentEngineFactory.getAssessment(
      basePath,
      userId,
      token,
      productConfigurationId,
      assessmentId,
      locale,
      parentAssessmentId,
    );

    const assessmentType = assessmentFullRead.productConfiguration.assessmentType;

    if (assessmentType === AssessmentTypeIri.APTITUDES_ASSESSMENT_TYPE_IRI) {
      const achieveWorksReportId = getIdFromIri(assessmentFullRead.achieveWorksReport);

      return ReportEngine.getReportEngine(
        basePath,
        userId,
        assessmentFullRead.status,
        token,
        achieveWorksReportId,
        hasCareers,
        hasBadges,
        enableReportBlocking,
        assessmentFullRead.isResultReviewed,
        locale,
      );
    }

    if (assessmentType === AssessmentTypeIri.PERSONALITY_ASSESSMENT_TYPE_IRI) {
      return this.getPersonalityAssessmentEngine(assessmentClient, assessmentFullRead, userId, token, hasCareers, hasBadges);
    }

    if (AssessmentTypeIri.APTITUDE_SECTION_ASSESSMENTS.includes(assessmentType)) {
      return new ChildAssessmentEngine(userId, assessmentClient, assessmentFullRead, hasCareers, hasBadges);
    }

    return new AssessmentEngine(userId, assessmentClient, assessmentFullRead, hasCareers, hasBadges);
  }
}
